import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Frequency as GalaxyFrequency } from '@vendasta/galaxy/frequency';
import { AppKey, AppPrice, UnitBillingFrequency } from '@galaxy/marketplace-apps';
import { convertAppsFrequencyToGalaxyFrequency } from '@vendasta/store';

export interface DialogInput {
  targetProduct: Product;
  affectedProducts: Product[];
  otherAffectedProducts?: Product[];
  dialogTextPaths?: {
    title: string;
    description: string;
    otherDescription?: string;
    confirmation: string;
    confirmButton: string;
  };
}

export interface Product {
  name: string;
  icon?: string;
  currency?: string;
  price?: number;
  frequency?: GalaxyFrequency;
  isStartingAt?: boolean;
}

export interface Edition {
  appKey: AppKey;
  name: string;
}

@Component({
  templateUrl: 'product-requirements-dialog.component.html',
  styleUrls: ['product-requirements-dialog.component.scss'],
  standalone: false,
})
export class ProductRequirementsDialogComponent implements OnInit {
  targetProduct: Product;
  affectedProducts: Product[];
  otherAffectedProducts: Product[];

  GalaxyFrequency = GalaxyFrequency;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogInput,
    private dialogRef: MatDialogRef<boolean>,
  ) {}

  ngOnInit(): void {
    this.targetProduct = this.data.targetProduct;
    this.affectedProducts = this.data.affectedProducts;
    this.otherAffectedProducts = this.data.otherAffectedProducts;
  }

  confirm(): void {
    this.dialogRef.close(true);
  }
}

export function marketplaceAppToDialogProduct(
  name: string,
  icon: string,
  editions: Edition[],
  requiredAppKey?: AppKey,
  currency?: string,
  appPricing?: AppPrice,
): Product {
  let editionSummaries = [];
  editions = editions ?? [];
  if (editions.length < 1) {
    const appPrice = appPricing.pricesForEditions['']?.pricesForCurrencies[currency]?.pricesForFrequencies[0];
    const price = appPrice?.pricingRules[0]?.price ?? 0;
    const frequency = appPrice?.frequency ?? UnitBillingFrequency.UNIT_BILLING_FREQUENCY_MONTHLY;
    editionSummaries = [
      {
        price: price,
        frequency: convertAppsFrequencyToGalaxyFrequency(frequency),
      },
    ];
  } else {
    // If a specific edition is required, only consider that edition.
    if (requiredAppKey.editionId || requiredAppKey.editionId === '') {
      editions = editions.filter((e) => (e.appKey.editionId ?? '') === requiredAppKey.editionId);
    }

    editionSummaries = editions.map((e) => {
      const appPrice =
        appPricing.pricesForEditions[e.appKey.editionId ?? '']?.pricesForCurrencies[currency]?.pricesForFrequencies[0];
      const price = appPrice?.pricingRules[0]?.price ?? 0;
      const frequency = appPrice?.frequency ?? UnitBillingFrequency.UNIT_BILLING_FREQUENCY_MONTHLY;
      return {
        name: e.name,
        price: price,
        frequency: convertAppsFrequencyToGalaxyFrequency(frequency),
      };
    });
  }
  let editionSummary = editionSummaries[0];
  if (editionSummaries.length > 1) {
    editionSummaries.forEach((es) => {
      if (es.price < editionSummary.price) {
        editionSummary = es;
      }
    });
  }

  return {
    name: !requiredAppKey.editionId && requiredAppKey.editionId !== '' ? name : `${name} | ${editionSummary.name}`,
    icon: icon,
    price: editionSummary.price,
    currency: currency,
    frequency: editionSummary.frequency,
    isStartingAt: editionSummaries.length > 1,
  };
}
